import _ from "lodash"
import { customValidators } from "./WrapperField"
import { FORM_TYPE } from "../../pages/Form/forms/IPlushelper"
import { getNumericRangeError } from "./numericValidation"

const FORMAT_STYLE_REQUIRED_MESSAGE_PATTERNS = [
    /^please enter valid\b/i,
    /^please enter a valid\b/i,
    /^invalid\b/i,
];

const GENERIC_REQUIRED_MESSAGE_PATTERNS = [
    /^this field is required\.?$/i,
];

const getFieldLabelForRequiredMessage = (jsonField = {}) => {
    const rawLabel = typeof jsonField?.label === "string" ? jsonField.label.trim() : "";
    if (rawLabel) {
        return rawLabel.replace(/\s+$/, "");
    }

    const rawField = typeof jsonField?.field === "string" ? jsonField.field : "";
    const fieldSegment = rawField.split(".").pop()?.replace(/\[\]/g, "") || "field";
    return _.startCase(fieldSegment);
};

const getInferredRequiredMessage = (jsonField = {}) => {
    const label = getFieldLabelForRequiredMessage(jsonField);
    const fieldName = typeof jsonField?.field === "string"
        ? jsonField.field.split(".").pop()?.replace(/\[\]/g, "")
        : "";
    const uiType = jsonField?.uiType;
    const type = jsonField?.type;

    if (uiType === "radioBoolean") {
        return "Please select Yes or No.";
    }

    if (
        uiType === "select"
        || uiType === "radioMultiple"
        || uiType === "checkboxMultiple"
        || type === "singleSelection"
        || type === "multipleSelection"
        || type === "checkbox"
    ) {
        return `Please select ${label}.`;
    }

    if (fieldName === "dob" || uiType === "date") {
        return `Please select ${label}.`;
    }

    if (type === "boolean") {
        return `Please answer ${label}.`;
    }

    return `Please enter ${label}.`;
};

export const getRequiredErrorMessage = (jsonField, fallback = "This field is required.") => {
    const explicitRequiredMessage = jsonField?.errorTypes?.required;
    if (explicitRequiredMessage) {
        return explicitRequiredMessage;
    }

    const schemaErrorMessage = typeof jsonField?.error === "string"
        ? jsonField.error.trim()
        : "";

    if (!schemaErrorMessage) {
        return GENERIC_REQUIRED_MESSAGE_PATTERNS.some((pattern) => pattern.test(fallback))
            ? getInferredRequiredMessage(jsonField)
            : fallback;
    }

    if (FORMAT_STYLE_REQUIRED_MESSAGE_PATTERNS.some((pattern) => pattern.test(schemaErrorMessage))) {
        return GENERIC_REQUIRED_MESSAGE_PATTERNS.some((pattern) => pattern.test(fallback))
            ? getInferredRequiredMessage(jsonField)
            : fallback;
    }

    return schemaErrorMessage || (
        GENERIC_REQUIRED_MESSAGE_PATTERNS.some((pattern) => pattern.test(fallback))
            ? getInferredRequiredMessage(jsonField)
            : fallback
    );
};

export const resolveFieldPath = (fieldPath, arrayIndexing = null) => {
    if (!fieldPath) return fieldPath;

    // Handle multiple array placeholders if needed
    if (fieldPath.includes('[]') && arrayIndexing !== null && arrayIndexing !== undefined) {
        return fieldPath.replace(/\[\]/g, `[${arrayIndexing}]`);
    }

    return fieldPath;
};

const normalizeFieldIdSegment = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return String(value)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
};

const buildCompositeRowKey = (values = []) => {
    const parts = values
        .map((value) => normalizeFieldIdSegment(value))
        .filter(Boolean);
    if (!parts.length) {
        return null;
    }
    return parts.join("-");
};

const resolveIndividualRowKey = (rowData = {}, fallbackRowKey = null) => {
    return buildCompositeRowKey([rowData?._id])
        || buildCompositeRowKey([rowData?.aEId])
        || buildCompositeRowKey([rowData?.cyclosUserId])
        || (fallbackRowKey !== null && fallbackRowKey !== undefined ? String(fallbackRowKey) : null);
};

const resolveEntityRowKey = (rowData = {}, fallbackRowKey = null) => (
    buildCompositeRowKey([rowData?.aEId])
        || buildCompositeRowKey([rowData?._id])
        || (fallbackRowKey !== null && fallbackRowKey !== undefined ? String(fallbackRowKey) : null)
);

const resolveAddressRowKey = (rowData = {}, fallbackRowKey = null) => {
    return buildCompositeRowKey([rowData?.type])
        || buildCompositeRowKey([rowData?._id])
        || (fallbackRowKey !== null && fallbackRowKey !== undefined ? String(fallbackRowKey) : null);
};

const resolveStableArrayRowKey = (fieldPath, arrayIndexing = null, dataObj = null) => {
    if (!fieldPath?.includes("[]") || arrayIndexing === null || arrayIndexing === undefined || !dataObj) {
        return arrayIndexing;
    }

    const arrayFieldPath = fieldPath.split("[]")[0];
    const arrayFieldName = arrayFieldPath?.split(".")?.pop();
    const collection = _.get(dataObj, arrayFieldPath);
    const rowData = Array.isArray(collection) ? collection[arrayIndexing] : null;

    if (!rowData || typeof rowData !== "object") {
        return arrayIndexing;
    }

    let stableRowKey = null;
    if (arrayFieldName === "individuals") {
        stableRowKey = resolveIndividualRowKey(rowData, arrayIndexing);
    } else if (arrayFieldName === "entities") {
        stableRowKey = resolveEntityRowKey(rowData, arrayIndexing);
    } else if (arrayFieldName === "addressList") {
        stableRowKey = resolveAddressRowKey(rowData, arrayIndexing);
    }

    return stableRowKey || arrayIndexing;
};

const buildFallbackFieldId = (jsonField = {}, arrayIndexing = null) => {
    const segments = [
        jsonField?.wizard || "unknown-wizard",
        jsonField?.field || jsonField?.identifier || jsonField?.label || jsonField?.parentField || jsonField?.uiType || "field",
    ];

    if (arrayIndexing !== null && arrayIndexing !== undefined) {
        segments.push(String(arrayIndexing));
    }

    const normalized = segments
        .map((value) => normalizeFieldIdSegment(value))
        .filter(Boolean)
        .join("-");

    return normalized || "unknown-field";
};

/**
 * Generate a stable, unique ID for form fields that won't change
 * Format: ${wizard}-${fieldName} with array indexing support
 * @param {Object} jsonField - The field configuration object
 * @param {number|null} arrayIndexing - Array index if field is in array
 * @param {Object|null} dataObj - Current form object to derive stable row identity
 * @returns {string} - Stable ID for the field
 */
export const generateFieldId = (jsonField, arrayIndexing = null, dataObj = null) => {
    if (!jsonField?.field || !jsonField?.wizard) {
        console.warn('generateFieldId: Missing field or wizard in jsonField', jsonField);
        return buildFallbackFieldId(jsonField, arrayIndexing);
    }

    const wizard = jsonField.wizard;
    let fieldName = jsonField.field;

    // Handle array indexing - replace [] with actual index
    if (fieldName.includes('[]') && arrayIndexing !== null && arrayIndexing !== undefined) {
        const stableRowKey = resolveStableArrayRowKey(fieldName, arrayIndexing, dataObj);
        fieldName = fieldName.replace(/\[\]/g, `[${stableRowKey}]`);
    }

    // Replace dots and array brackets with hyphens for valid HTML IDs
    // Examples:
    // individuals[0].firstName -> individuals-0-firstName  
    // address.street -> address-street
    // Business_Name -> Business_Name
    const sanitizedFieldName = fieldName
        .replace(/\[([^\]]+)\]/g, '-$1')
        .replace(/\./g, '-')          // . -> -
        .replace(/_{2,}/g, '_')       // Multiple underscores -> single
        .replace(/-{2,}/g, '-');      // Multiple hyphens -> single

    return `${wizard}-${sanitizedFieldName}`;
};

export const isValidList = (list, minLength = 0) => {
    if (Array.isArray(list) && list.length >= minLength) {
        return true
    }
    return false
}

const USA_LOCATION_VALUES = new Set([
    "usa",
    "us"
]);

const normalizeLocationValue = (value) => {
    if (value === null || value === undefined) return "";
    return String(value).trim().toLowerCase();
};

const hasUSA = (locations = []) => {
    return (locations || []).some((v) => USA_LOCATION_VALUES.has(normalizeLocationValue(v)));
};

const hasInternational = (locations = []) => {
    return (locations || []).some((v) => {
        const normalized = normalizeLocationValue(v);
        return normalized && !USA_LOCATION_VALUES.has(normalized);
    });
};

const toNumber = (value) => {
    const normalizedValue = typeof value === 'string'
        ? value.replace(/,/g, '').trim()
        : value;
    const num = Number(normalizedValue);
    return Number.isFinite(num) ? num : 0;
};

const isPositive = (value) => toNumber(value) > 0;

const ACCOUNT_ACTIVITY_COMPATIBILITY_FIELD_MAP = Object.freeze({
    Count_Of_Monthly_Stablecoin_Conversions_Deposits: [
        "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
    ],
    Number_Of_Monthly_Stablecoin_Conversions_Deposits: [
        "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
    ],
    Count_Of_Monthly_Stablecoin_Conversions_Payments: [
        "Number_Of_Monthly_Stablecoin_Conversions_Payments",
    ],
    Number_Of_Monthly_Stablecoin_Conversions_Payments: [
        "Count_Of_Monthly_Stablecoin_Conversions_Payments",
    ],
    Expected_Amount_Monthly_Incoming_USDC: [
        "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
    ],
    Amount_Of_Monthly_Stablecoin_Conversions_Deposits: [
        "Expected_Amount_Monthly_Incoming_USDC",
    ],
    Expected_Amount_Monthly_Outgoing_USDC: [
        "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
    ],
    Amount_Of_Monthly_Stablecoin_Conversions_Payments: [
        "Expected_Amount_Monthly_Outgoing_USDC",
    ],
});

const getCompatibleAccountActivityFields = (field) => {
    if (!field) return [];

    return [
        field,
        ...(ACCOUNT_ACTIVITY_COMPATIBILITY_FIELD_MAP[field] || []),
    ];
};

export const getFieldValue = (data, field) => {
    const fieldCandidates = getCompatibleAccountActivityFields(field);

    for (const fieldCandidate of fieldCandidates) {
        if (!fieldCandidate) continue;

        const byPath = _.get(data, fieldCandidate);
        if (byPath !== undefined) return byPath;

        if (data && Object.prototype.hasOwnProperty.call(data, fieldCandidate)) {
            return data[fieldCandidate];
        }
    }

    return undefined;
};

const getLocationsValue = (data, fieldCandidates) => {
    for (const field of fieldCandidates) {
        const value = getFieldValue(data, field);
        if (Array.isArray(value)) return value;
    }
    return [];
};

const getLocationsFieldName = (data, fieldCandidates) => {
    for (const field of fieldCandidates) {
        const value = getFieldValue(data, field);
        if (value !== undefined) return field;
    }
    return fieldCandidates[0];
};

const isEmptyAccountActivityValue = (value) => (
    value === undefined
    || value === null
    || value === ""
);

export const EMPLOYED_EMPLOYMENT_STATUSES = ["Employed", "Employed_Variable_Wages"];

export const isEmploymentStatusEmployed = (employmentStatus) =>
    EMPLOYED_EMPLOYMENT_STATUSES.includes(employmentStatus);

export const POSITIVE_INCOME_EMPLOYMENT_STATUSES = [
    ...EMPLOYED_EMPLOYMENT_STATUSES,
    "Self_Employed",
];

export const isEmploymentStatusRequiringPositiveIncome = (employmentStatus) =>
    POSITIVE_INCOME_EMPLOYMENT_STATUSES.includes(employmentStatus);

export const getAccountActivityRuleErrors = (data, options = {}) => {
    const skipWhenInterestedInPrime = options.skipWhenInterestedInPrime !== false;
    const deferLocationErrorsUntilActivityFieldsFilled = options.deferLocationErrorsUntilActivityFieldsFilled === true;
    if (skipWhenInterestedInPrime && data?.interestedInPrime) return [];
    const depositLocationFields = options.depositLocationFields || ["Deposit_Locations", "depositLocations"];
    const paymentLocationFields = options.paymentLocationFields || ["Payment_Locations", "paymentLocations"];

    const DOMESTIC_DEPOSIT_FIELDS = [
        "Number_Of_Monthly_Ach_Deposits",
        "Amount_Of_Monthly_Ach_Deposits",
        "Number_Of_Monthly_Domestic_Wire_Deposits",
        "Amount_Of_Monthly_Domestic_Wire_Deposits"
    ];

    const DOMESTIC_PAYMENT_FIELDS = [
        "Number_Of_Monthly_Ach_Payments",
        "Amount_Of_Monthly_Ach_Payments",
        "Number_Of_Monthly_Domestic_Wire_Payments",
        "Amount_Of_Monthly_Domestic_Wire_Payments"
    ];

    const INTERNATIONAL_DEPOSIT_FIELDS = [
        "Number_Of_Monthly_International_Wire_Deposits",
        "Amount_Of_Monthly_International_Wire_Deposits"
    ];

    const INTERNATIONAL_PAYMENT_FIELDS = [
        "Number_Of_Monthly_International_Wire_Payments",
        "Amount_Of_Monthly_International_Wire_Payments"
    ];

    const getPositiveFields = (fields) => fields.filter((field) => isPositive(getFieldValue(data, field)));

    const depositLocations = getLocationsValue(data, depositLocationFields);
    const paymentLocations = getLocationsValue(data, paymentLocationFields);
    const depositFieldName = getLocationsFieldName(data, depositLocationFields);
    const paymentFieldName = getLocationsFieldName(data, paymentLocationFields);

    const domesticDepositFieldsPositive = getPositiveFields(DOMESTIC_DEPOSIT_FIELDS);
    const domesticPaymentFieldsPositive = getPositiveFields(DOMESTIC_PAYMENT_FIELDS);
    const internationalDepositFieldsPositive = getPositiveFields(INTERNATIONAL_DEPOSIT_FIELDS);
    const internationalPaymentFieldsPositive = getPositiveFields(INTERNATIONAL_PAYMENT_FIELDS);

    const hasDomesticDeposits = domesticDepositFieldsPositive.length > 0;
    const hasDomesticPayments = domesticPaymentFieldsPositive.length > 0;
    const hasInternationalDeposits = internationalDepositFieldsPositive.length > 0;
    const hasInternationalPayments = internationalPaymentFieldsPositive.length > 0;

    const errors = [];
    const getCompatibleActivityFields = (fields = []) => (
        [...new Set(
            (Array.isArray(fields) ? fields : [])
                .flatMap((field) => getCompatibleAccountActivityFields(field))
                .filter(Boolean)
        )]
    );

    if (hasDomesticDeposits && !hasUSA(depositLocations)) {
        errors.push({
            field: depositFieldName,
            message: "USA must be selected in deposit locations when ACH or Domestic Wire deposits are greater than 0.",
            activityFields: getCompatibleActivityFields(
                domesticDepositFieldsPositive.length ? domesticDepositFieldsPositive : DOMESTIC_DEPOSIT_FIELDS
            )
        });
    }
    if (hasDomesticPayments && !hasUSA(paymentLocations)) {
        errors.push({
            field: paymentFieldName,
            message: "USA must be selected in payment locations when ACH or Domestic Wire payments are greater than 0.",
            activityFields: getCompatibleActivityFields(
                domesticPaymentFieldsPositive.length ? domesticPaymentFieldsPositive : DOMESTIC_PAYMENT_FIELDS
            )
        });
    }
    if (hasInternationalDeposits && !hasInternational(depositLocations)) {
        errors.push({
            field: depositFieldName,
            message: "At least one non-USA location must be selected in deposit locations when International Wire deposits are greater than 0.",
            activityFields: getCompatibleActivityFields(
                internationalDepositFieldsPositive.length ? internationalDepositFieldsPositive : INTERNATIONAL_DEPOSIT_FIELDS
            )
        });
    }
    if (hasInternationalPayments && !hasInternational(paymentLocations)) {
        errors.push({
            field: paymentFieldName,
            message: "At least one non-USA location must be selected in payment locations when International Wire payments are greater than 0.",
            activityFields: getCompatibleActivityFields(
                internationalPaymentFieldsPositive.length ? internationalPaymentFieldsPositive : INTERNATIONAL_PAYMENT_FIELDS
            )
        });
    }

    if (hasUSA(depositLocations) && !hasDomesticDeposits) {
        errors.push({
            field: depositFieldName,
            message: "ACH or Domestic Wire deposit values must be greater than 0 when USA is selected in deposit locations.",
            activityFields: getCompatibleActivityFields(DOMESTIC_DEPOSIT_FIELDS)
        });
    }
    if (hasUSA(paymentLocations) && !hasDomesticPayments) {
        errors.push({
            field: paymentFieldName,
            message: "ACH or Domestic Wire payment values must be greater than 0 when USA is selected in payment locations.",
            activityFields: getCompatibleActivityFields(DOMESTIC_PAYMENT_FIELDS)
        });
    }
    if (hasInternational(depositLocations) && !hasInternationalDeposits) {
        errors.push({
            field: depositFieldName,
            message: "International Wire deposit values must be greater than 0 when non-USA locations are selected in deposit locations.",
            activityFields: getCompatibleActivityFields(INTERNATIONAL_DEPOSIT_FIELDS)
        });
    }
    if (hasInternational(paymentLocations) && !hasInternationalPayments) {
        errors.push({
            field: paymentFieldName,
            message: "International Wire payment values must be greater than 0 when non-USA locations are selected in payment locations.",
            activityFields: getCompatibleActivityFields(INTERNATIONAL_PAYMENT_FIELDS)
        });
    }

    const pairRules = [
        {
            numberField: "Number_Of_Monthly_Ach_Deposits",
            amountField: "Amount_Of_Monthly_Ach_Deposits",
            label: "ACH deposits"
        },
        {
            numberField: "Number_Of_Monthly_Domestic_Wire_Deposits",
            amountField: "Amount_Of_Monthly_Domestic_Wire_Deposits",
            label: "Domestic Wire deposits"
        },
        {
            numberField: "Number_Of_Monthly_International_Wire_Deposits",
            amountField: "Amount_Of_Monthly_International_Wire_Deposits",
            label: "International Wire deposits"
        },
        {
            numberField: "Number_Of_Monthly_Ach_Payments",
            amountField: "Amount_Of_Monthly_Ach_Payments",
            label: "ACH payments"
        },
        {
            numberField: "Number_Of_Monthly_Domestic_Wire_Payments",
            amountField: "Amount_Of_Monthly_Domestic_Wire_Payments",
            label: "Domestic Wire payments"
        },
        {
            numberField: "Number_Of_Monthly_International_Wire_Payments",
            amountField: "Amount_Of_Monthly_International_Wire_Payments",
            label: "International Wire payments"
        },
        {
            numberField: "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
            amountField: "Expected_Amount_Monthly_Incoming_USDC",
            label: "Stablecoin conversions (Deposits)"
        },
        {
            numberField: "Count_Of_Monthly_Stablecoin_Conversions_Payments",
            amountField: "Expected_Amount_Monthly_Outgoing_USDC",
            label: "Stablecoin conversions (Payments)"
        },
        {
            numberField: "Count_Of_Monthly_Stablecoin_Conversions",
            amountField: "Amount_Of_Monthly_Stablecoin_Conversions",
            label: "Stablecoin conversions"
        },
        {
            numberField: "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
            amountField: "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
            label: "Stablecoin conversions (Deposits)"
        },
        {
            numberField: "Number_Of_Monthly_Stablecoin_Conversions_Payments",
            amountField: "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
            label: "Stablecoin conversions (Payments)"
        },
        {
            numberField: "Count_Of_Monthly_Cryptocurrency_Conversions",
            amountField: "Amount_Of_Monthly_Cryptocurrency_Conversions",
            label: "Cryptocurrency conversions"
        }
    ];

    const positivePlanFieldRules = [
        {
            planField: "hasCoinConversionPlan",
            targetField: "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
            message: "Please enter a value greater than 0 for Expected count of monthly stablecoin conversions deposits when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Expected_Amount_Monthly_Incoming_USDC",
            message: "Please enter a value greater than 0 for Expected amount of monthly stablecoin conversions deposits (USD) when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Count_Of_Monthly_Stablecoin_Conversions_Payments",
            message: "Please enter a value greater than 0 for Expected count of monthly stablecoin conversions payments when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Expected_Amount_Monthly_Outgoing_USDC",
            message: "Please enter a value greater than 0 for Expected amount of monthly stablecoin conversions payments (USD) when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
            message: "Please enter a value greater than 0 for Expected count of monthly stablecoin conversions deposits when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
            message: "Please enter a value greater than 0 for Expected amount of monthly stablecoin conversions deposits (USD) when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Number_Of_Monthly_Stablecoin_Conversions_Payments",
            message: "Please enter a value greater than 0 for Expected count of monthly stablecoin conversions payments when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCoinConversionPlan",
            targetField: "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
            message: "Please enter a value greater than 0 for Expected amount of monthly stablecoin conversions payments (USD) when you selected \"Yes\" for stablecoin conversions.",
        },
        {
            planField: "hasCryptoConversionPlan",
            targetField: "Count_Of_Monthly_Cryptocurrency_Conversions",
            message: "Please enter a value greater than 0 for Expected count of monthly cryptocurrency conversions when you selected \"Yes\" for cryptocurrency conversions.",
        },
        {
            planField: "hasCryptoConversionPlan",
            targetField: "Amount_Of_Monthly_Cryptocurrency_Conversions",
            message: "Please enter a value greater than 0 for Expected amount of monthly cryptocurrency conversions (USD) when you selected \"Yes\" for cryptocurrency conversions.",
        },
    ];

    pairRules.forEach(({ numberField, amountField, label }) => {
        const numberValue = getFieldValue(data, numberField);
        const amountValue = getFieldValue(data, amountField);
        if (isPositive(numberValue) && !isPositive(amountValue)) {
            errors.push({
                field: amountField,
                activityFields: getCompatibleActivityFields([amountField]),
                message: `Expected amount of monthly ${label} must be greater than 0 when the number is greater than 0.`
            });
        }
        if (isPositive(amountValue) && !isPositive(numberValue)) {
            errors.push({
                field: numberField,
                activityFields: getCompatibleActivityFields([numberField]),
                message: `Expected number of monthly ${label} must be greater than 0 when the amount is greater than 0.`
            });
        }
    });

    positivePlanFieldRules.forEach(({ planField, targetField, message }) => {
        if (getFieldValue(data, planField) !== true) {
            return;
        }

        const targetValue = getFieldValue(data, targetField);
        if (isEmptyAccountActivityValue(targetValue) || isPositive(targetValue)) {
            return;
        }

        errors.push({
            field: targetField,
            activityFields: getCompatibleActivityFields([targetField]),
            message,
        });
    });

    if (!deferLocationErrorsUntilActivityFieldsFilled) {
        return errors;
    }

    return errors.filter((error) => {
        if (!error?.field || !Array.isArray(error?.activityFields) || !error.activityFields.length) {
            return true;
        }

        const isLocationError = !error.activityFields.includes(error.field);
        if (!isLocationError) {
            return true;
        }

        return !error.activityFields.some((activityField) => isEmptyAccountActivityValue(getFieldValue(data, activityField)));
    });
};


const ACCOUNT_ACTIVITY_FIELDS = new Set([
    "Number_Of_Monthly_Ach_Deposits",
    "Amount_Of_Monthly_Ach_Deposits",
    "Number_Of_Monthly_Domestic_Wire_Deposits",
    "Amount_Of_Monthly_Domestic_Wire_Deposits",
    "Number_Of_Monthly_International_Wire_Deposits",
    "Amount_Of_Monthly_International_Wire_Deposits",
    "Number_Of_Monthly_Ach_Payments",
    "Amount_Of_Monthly_Ach_Payments",
    "Number_Of_Monthly_Domestic_Wire_Payments",
    "Amount_Of_Monthly_Domestic_Wire_Payments",
    "Number_Of_Monthly_International_Wire_Payments",
    "Amount_Of_Monthly_International_Wire_Payments",
    "Deposit_Locations",
    "Payment_Locations",
    "depositLocations",
    "paymentLocations",
    // Stablecoin / Crypto fields
    "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Expected_Amount_Monthly_Incoming_USDC",
    "Count_Of_Monthly_Stablecoin_Conversions_Payments",
    "Expected_Amount_Monthly_Outgoing_USDC",
    "Count_Of_Monthly_Stablecoin_Conversions",
    "Amount_Of_Monthly_Stablecoin_Conversions",
    "Number_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Amount_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Number_Of_Monthly_Stablecoin_Conversions_Payments",
    "Amount_Of_Monthly_Stablecoin_Conversions_Payments",
    "Count_Of_Monthly_Cryptocurrency_Conversions",
    "Amount_Of_Monthly_Cryptocurrency_Conversions"
]);

const ACCOUNT_ACTIVITY_WIZARDS = new Set([
    "AccountActivity",
    "IPlusAccountActivityDeclaration",
    "BPlusAccountActivityDeclaration",
    3,
    "3"
]);

const isAccountActivityWizard = (wizard) => {
    if (wizard === undefined || wizard === null) return false;
    return ACCOUNT_ACTIVITY_WIZARDS.has(wizard);
};

export const getAccountActivityFieldError = (jsonField, data) => {
    // Check if this field is one of the watched fields for account activity validation
    if (!jsonField?.field || !ACCOUNT_ACTIVITY_FIELDS.has(jsonField.field)) return "";
    const isExplicitValidator = jsonField?.validator === "accountActivityCrossField";
    if (!isExplicitValidator && !isAccountActivityWizard(jsonField?.wizard)) return "";

    const fieldName = jsonField.field;
    const shouldForcePrimeValidation = (
        data?.formType === FORM_TYPE.BusinessPrime
        || (
            data?.formType === FORM_TYPE.Business
            && [2, 3].includes(Number(data?.type))
        )
    );
    const errors = getAccountActivityRuleErrors(data, {
        skipWhenInterestedInPrime: shouldForcePrimeValidation ? false : undefined,
        deferLocationErrorsUntilActivityFieldsFilled: true,
    });
    const match = errors.find((err) => err.field === fieldName || (Array.isArray(err.activityFields) && err.activityFields.includes(fieldName)));
    return match ? match.message : "";
};


const showIfValidator = {
    southAmericaValidator: function (dataObj) {
        if (dataObj?.Deposit_Locations?.includes("south_america") || dataObj?.Payment_Locations?.includes("south_america")) {
            return true
        }
        // For Individual Plus
        if (dataObj?.depositLocations?.includes("south_america") || dataObj?.paymentLocations?.includes("south_america")) {
            return true
        }
        // If the data already present then also show the fields
        // if (isValidList(dataObj?.Interaction_With_South_American_Countries, 0)) {
        //     return true;
        // }
        return false
    },
    centralAmericaValidator: function (dataObj) {
        if (dataObj?.Deposit_Locations?.includes("central_america") || dataObj?.Payment_Locations?.includes("central_america")) {
            return true
        }
        // For Individual Plus
        if (dataObj?.depositLocations?.includes("central_america") || dataObj?.paymentLocations?.includes("central_america")) {
            return true
        }
        // If the data already present then also show the fields
        // if (isValidList(dataObj?.Interaction_With_Central_American_Countries, 0)) {
        //     return true;
        // }
        return false
    },
    caribbeanValidator: function (dataObj) {
        if (dataObj?.Deposit_Locations?.includes("caribbean") || dataObj?.Payment_Locations?.includes("caribbean")) {
            return true
        }
        // For Individual Plus
        if (dataObj?.depositLocations?.includes("caribbean") || dataObj?.paymentLocations?.includes("caribbean")) {
            return true
        }
        // If the data already present then also show the fields
        // if (isValidList(dataObj?.Interaction_With_Caribbean_Countries, 0)) {
        //     return true;
        // }
        return false
    },
    IPrimeSouthAmericaValidator: function (dataObj) {
        if (dataObj?.formType !== FORM_TYPE.IndividualPrime) return false;
        return this.southAmericaValidator(dataObj);
    },
    IPrimeCentralAmericaValidator: function (dataObj) {
        if (dataObj?.formType !== FORM_TYPE.IndividualPrime) return false;
        return this.centralAmericaValidator(dataObj);
    },
    IPrimeCaribbeanValidator: function (dataObj) {
        if (dataObj?.formType !== FORM_TYPE.IndividualPrime) return false;
        return this.caribbeanValidator(dataObj);
    },
    hasAnnualStatementValidator: (dataObj) => {
        if (dataObj?.hasAuditStatements == false) {
            return true
        }
        return false
    },
    hasFinancialForecastValidator: (dataObj) => {
        if (dataObj?.hasAnnualStatements == false) {
            return true
        }
        return false
    },
    isNationalityNotAmerican: (dataObj) => {
        return !(["USA", "US"]?.includes(dataObj?.nationality) || ["USA", "US"]?.includes(dataObj?.address?.country))
    },
    isNationalityAmerican: (dataObj) => {
        return (["USA", "US"]?.includes(dataObj?.nationality) || ["USA", "US"]?.includes(dataObj?.address?.country))
    },
    isFormTypeIPrime: (dataObj) => {
        return dataObj?.formType === FORM_TYPE.IndividualPrime
    },
    isFormTypeIPlus: (dataObj) => {
        return dataObj?.formType === FORM_TYPE.IndividualPlus
    },
    isFormTypeBPrime: (dataObj) => {
        return dataObj?.formType === FORM_TYPE.BusinessPrime
            || (dataObj?.formType === FORM_TYPE.Business && [2, 3].includes(Number(dataObj?.type)));
    },
    isEmploymentStatusRetiredOrUnemployed: (dataObj) => {
        return dataObj?.employmentStatus == "Retired" || dataObj?.employmentStatus == "Unemployed"
    }
}

/**
 * compares two values (arrays, primitives, or objects) to determine if they are "the same".
 * @param {*} newValue   The current value to compare.
 * @param {*} prevValue  The previous value to compare against.
 * @returns {boolean}    True if values are considered equal, false otherwise.
 */
const isPrevValueSame = (newValue, prevValue) => {
    const normalizedNextValue = normalizeBooleanLikeValue(newValue);
    const normalizedPrevValue = normalizeBooleanLikeValue(prevValue);

    // Both are arrays: check length, then ensure no differences
    if (Array.isArray(normalizedNextValue) && Array.isArray(normalizedPrevValue)) {
        return normalizedNextValue.length === normalizedPrevValue.length &&
            _.isEmpty(_.xorWith(normalizedNextValue, normalizedPrevValue, _.isEqual)); // xorWith returns entries only in one of the arrays; empty means same elements
    }
    // Neither is an object (i.e., both are primitives like string, number, boolean, null, undefined)
    if (!_.isObject(normalizedNextValue) && !_.isObject(normalizedPrevValue)) {
        return normalizedNextValue === normalizedPrevValue;
    }
    // Everything else (objects, Date, RegExp, mixed types): deep compare
    return _.isEqual(normalizedNextValue, normalizedPrevValue);
};

const normalizePercentValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return '';
        const stripped = trimmed.endsWith('%') ? trimmed.slice(0, -1).trim() : trimmed;
        const num = Number(stripped);
        if (!Number.isNaN(num)) return num;
        return trimmed;
    }
    return value;
};

const isPercentValueSame = (newValue, prevValue) => {
    const normalizedNew = normalizePercentValue(newValue);
    const normalizedPrev = normalizePercentValue(prevValue);
    return _.isEqual(normalizedNew, normalizedPrev);
};

const isPercentOwnerField = (resolvedPath = '') => {
    if (!resolvedPath) return false;
    return resolvedPath === 'percentOwner' || resolvedPath.endsWith('.percentOwner');
};

export const areFieldValuesEqual = (fieldPath, nextValue, previousValue) => {
    const compare = isPercentOwnerField(fieldPath) ? isPercentValueSame : isPrevValueSame;
    return compare(nextValue, previousValue);
};

export const normalizeBooleanLikeValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1') return true;
    if (value === 0 || value === '0') return false;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === 'yes') return true;
        if (normalized === 'false' || normalized === 'no') return false;
    }
    return value;
};

/**
 * Evaluates a single condition
 * @param {Object} data - The data object to evaluate against
 * @param {Object} condition - The condition object with field, condition, value
 * @param {number|null} arrayIndexing - Current array index
 * @returns {boolean} - Whether the condition is met
 */
const evaluateSingleCondition = (data, condition, arrayIndexing = null) => {
    // Handle validator conditions
    if (condition?.validator) {
        if (showIfValidator[condition.validator]) {
            return showIfValidator[condition.validator](data, arrayIndexing);
        }
        console.warn(`Validator '${condition.validator}' not found`);
        return true;
    }

    // Handle field-based conditions
    if (!condition?.field) return true;

    const { field, condition: operator, value } = condition;

    // Resolve field path with array indexing
    const resolvedField = resolveFieldPath(field, arrayIndexing);
    const fieldValue = readValue(data, resolvedField);
    const normalizedFieldValue = normalizeBooleanLikeValue(fieldValue);
    const normalizedConditionValue = normalizeBooleanLikeValue(value);

    switch (operator) {
        case "==":
        case "===":
        case "equals":
            return normalizedFieldValue === normalizedConditionValue;

        case "!=":
        case "!==":
        case "notEquals":
            return normalizedFieldValue !== normalizedConditionValue;

        case ">":
        case "greaterThan":
            return fieldValue > value;

        case ">=":
        case "greaterThanOrEquals":
            return fieldValue >= value;

        case "<":
        case "lessThan":
            return fieldValue < value;

        case "<=":
        case "lessThanOrEquals":
            return fieldValue <= value;

        case "includes":
            if (Array.isArray(fieldValue)) {
                return fieldValue.includes(value);
            }
            return fieldValue === value;

        case "notIncludes":
            if (Array.isArray(fieldValue)) {
                return !fieldValue.includes(value);
            }
            return fieldValue !== value;

        case "contains":
            return typeof fieldValue === 'string' && fieldValue.includes(value);

        case "notContains":
            return typeof fieldValue === 'string' && !fieldValue.includes(value);

        case "startsWith":
            return typeof fieldValue === 'string' && fieldValue.startsWith(value);

        case "endsWith":
            return typeof fieldValue === 'string' && fieldValue.endsWith(value);

        case "empty":
            return !fieldValue ||
                (Array.isArray(fieldValue) && fieldValue.length === 0) ||
                (typeof fieldValue === 'string' && fieldValue.trim() === '');

        case "notEmpty":
            return fieldValue &&
                (Array.isArray(fieldValue) ? fieldValue.length > 0 :
                    typeof fieldValue === 'string' ? fieldValue.trim() !== '' : true);

        case "exists":
            return fieldValue !== null && fieldValue !== undefined;

        case "notExists":
            return fieldValue === null || fieldValue === undefined;

        default:
            console.warn(`Unknown condition operator: ${operator}`);
            return true;
    }
};


/**
 * Main function to evaluate showIf conditions with support for complex logic
 * @param {Object} data - The data object to evaluate against
 * @param {Object|null} showIfConfig - The showIf configuration
 * @param {number|null} arrayIndexing - Current array index for [] replacement
 * @returns {boolean} - Whether the field should be shown
 */
export const shouldShowField = (data, showIfConfig, arrayIndexing = null) => {
    // If no showIf config, always show the field
    if (!showIfConfig) return true;

    // Handle legacy format with just validator
    if (showIfConfig.validator && !showIfConfig.field && !showIfConfig.and && !showIfConfig.or) {
        if (showIfValidator[showIfConfig.validator]) {
            return showIfValidator[showIfConfig.validator](data, arrayIndexing);
        }
        return true;
    }

    // Initialize result with main condition (if exists)
    let result = true;

    // Evaluate main condition if it has a field
    if (showIfConfig.field) {
        result = evaluateSingleCondition(data, {
            field: showIfConfig.field,
            condition: showIfConfig.condition,
            value: showIfConfig.value
        }, arrayIndexing);
    }

    // Evaluate AND conditions
    if (showIfConfig.and && Array.isArray(showIfConfig.and)) {
        const andResult = showIfConfig.and.every(condition =>
            evaluateSingleCondition(data, condition, arrayIndexing)
        );
        result = result && andResult;
    }

    // Evaluate OR conditions
    if (showIfConfig.or && Array.isArray(showIfConfig.or)) {
        const orResult = showIfConfig.or.some(condition =>
            evaluateSingleCondition(data, condition, arrayIndexing)
        );

        // If there's a main condition or AND conditions, OR acts as an alternative
        // If there's no main condition and no AND, OR becomes the primary condition
        if (showIfConfig.field || showIfConfig.and) {
            result = result || orResult;
        } else {
            result = orResult;
        }
    }

    // Handle "others" array for backward compatibility
    if (showIfConfig.others && Array.isArray(showIfConfig.others)) {
        const othersResult = showIfConfig.others.every(condition =>
            evaluateSingleCondition(data, condition, arrayIndexing)
        );
        result = result && othersResult;
    }

    return result;
};

// True when a field's visibility is gated by a `hidePinCode` flag (e.g. the postal-code field,
// hidden when a country has a fixed default such as Hong Kong -> 999077). Such a field is hidden
// precisely because it was just auto-filled with an authoritative value that must persist, so any
// clear-on-hide / strip-hidden logic (the WrapperField visibility reset AND the BusinessV2 autosave
// hidden-field clearing) must NOT wipe it. Walks field/and/or/others showIf trees and matches any
// field ref (case-insensitive) ending in "hidePinCode" - covers both address.hidePinCode and the
// array form addressList[].hidePinCode.
export const isHidePinCodeGatedShowIf = (showIfConfig) => {
    if (!showIfConfig || typeof showIfConfig !== 'object') {
        return false;
    }
    const fieldRefs = [];
    const collect = (node) => {
        if (!node || typeof node !== 'object') {
            return;
        }
        if (typeof node.field === 'string') {
            fieldRefs.push(node.field);
        }
        ['and', 'or', 'others'].forEach((key) => {
            if (Array.isArray(node[key])) {
                node[key].forEach(collect);
            }
        });
    };
    collect(showIfConfig);
    return fieldRefs.some((field) => field.toLowerCase().endsWith('hidepincode'));
};

export const readValue = (obj, fieldPath, arrayIndexing = null) => {
    if (!obj) return null;
    if (!fieldPath) return obj;

    const resolvedPath = resolveFieldPath(fieldPath, arrayIndexing);

    // Convert array notation for lodash get
    // Convert addressList[0].street to addressList.0.street
    const lodashPath = resolvedPath.replace(/\[(\d+)\]/g, '.$1');

    return _.get(obj, lodashPath, null);
};

/**
 * Recursively sets a property and (optionally) keeps track of the original.
 * @param {Object} newObj - The object to modify.
 * @param {String} fieldPath - The path to the property to set, e.g. "a.b.c".
 * @param {any} newValue - The value to set.
 * @param {Boolean} trackDiff - Whether to track changes in diffMap.
 * @param {any} baselineValue - The value to set in diffMap if the initial value is undefined.
 * @returns {Object} - The modified object.
 */
export const setValue = (newObj, fieldPath, newValue, trackDiff, baselineValue = "", arrayIndexing = null) => {
    const resolvedPath = resolveFieldPath(fieldPath, arrayIndexing);

    // Parse array notation from the path
    const parseArrayPath = (path) => {
        const match = path.match(/^([^[]+)\[(\d+)\](.*)$/);
        if (match) {
            return {
                arrayName: match[1],
                index: parseInt(match[2]),
                remainder: match[3].startsWith('.') ? match[3].substring(1) : match[3]
            };
        }
        return null;
    };

    // Handle array notation in the path
    const arrayMatch = parseArrayPath(resolvedPath);

    if (arrayMatch) {
        const { arrayName, index, remainder } = arrayMatch;

        // Ensure the array exists
        if (!newObj[arrayName]) {
            newObj[arrayName] = [];
        }

        // Ensure the array has enough elements
        while (newObj[arrayName].length <= index) {
            newObj[arrayName].push({});
        }

        // If there's a remainder path, recurse
        if (remainder) {
            // Track diff for array elements
            if (trackDiff && !newObj[arrayName][index].diffMap) {
                newObj[arrayName][index].diffMap = {};
            }

            setValue(newObj[arrayName][index], remainder, newValue, trackDiff, baselineValue);
        } else {
            // Direct assignment to array element
            newObj[arrayName][index] = newValue;
        }

        return { ...newObj };
    }

    // Original logic for non-array paths
    const dot = resolvedPath.indexOf(".");
    if (dot === -1) {
        const originalValue = newObj?.[resolvedPath] ?? baselineValue;
        if (trackDiff) {
            if (!newObj.diffMap) {
                newObj.diffMap = {};
            }

            if (!(resolvedPath in newObj.diffMap)) {
                newObj.diffMap[resolvedPath] = originalValue;
            }

            if (areFieldValuesEqual(resolvedPath, newValue, newObj.diffMap[resolvedPath])) {
                delete newObj.diffMap[resolvedPath];
            }
        }
        newObj[resolvedPath] = newValue;
    } else {
        let firstPart = resolvedPath.substring(0, dot);
        const secondPart = resolvedPath.substring(dot + 1);

        // Check if firstPart contains array notation
        const firstPartArray = parseArrayPath(firstPart);
        if (firstPartArray) {
            // Handle nested array notation
            const { arrayName, index } = firstPartArray;
            if (!newObj[arrayName]) {
                newObj[arrayName] = [];
            }
            while (newObj[arrayName].length <= index) {
                newObj[arrayName].push({});
            }
            setValue(newObj[arrayName][index], secondPart, newValue, trackDiff, baselineValue);
        } else {
            // Original nested object logic
            if (!newObj[firstPart]) {
                // Check if the next part is a number (array index)
                const nextPartMatch = secondPart.match(/^(\d+)/);
                if (nextPartMatch) {
                    newObj[firstPart] = [];
                } else {
                    newObj[firstPart] = {};
                }
            }
            setValue(newObj[firstPart], secondPart, newValue, trackDiff, baselineValue);
        }
    }
    return { ...newObj };
};


export const validateField = (jsonField, value, data) => {
    let error = "";
    const isEmptyValue = value === undefined || value === null || value === "" || value?.length == 0;
    const isRequiredField = Boolean(jsonField?.required || jsonField?.showIf?.required);

    if (isRequiredField && isEmptyValue) {
        error = getRequiredErrorMessage(jsonField, "This field is required.");
    } else if (!isEmptyValue && jsonField?.min && value?.length < jsonField.min) {
        error = jsonField?.errorTypes?.length || `Minimum length is ${jsonField?.min}.`;
    } else if (!isEmptyValue && jsonField?.regex && !new RegExp(jsonField?.regex).test(value)) {
        error = jsonField?.errorTypes?.format || jsonField.error || "Invalid format.";
    } else if (!isEmptyValue && jsonField.max && value?.length > jsonField.max) {
        error = jsonField?.errorTypes?.length || `Maximum length is ${jsonField?.max}.`;
    } else if (!isEmptyValue) {
        error = getNumericRangeError(jsonField, value, jsonField?.label);
    }
    // Custom validator logic
    if (jsonField.validator && customValidators?.[jsonField.validator]) {
        const customError = customValidators[jsonField.validator](data, jsonField.field, value, jsonField.label);
        if (customError) {
            error = customError;
        }
    }
    if (!error) {
        const noneOfTheAboveError = getNoneOfTheAboveSelectionError(jsonField, value);
        if (noneOfTheAboveError) {
            error = noneOfTheAboveError;
        }
    }
    if (!error) {
        const accountActivityError = getAccountActivityFieldError(jsonField, data);
        if (accountActivityError) {
            error = accountActivityError;
        }
    }
    return error;
};

export const getNoneOfTheAboveSelectionError = (jsonField, value) => {
    if (!Array.isArray(value) || value.length <= 1) {
        return "";
    }

    const hasNoneOfTheAboveOption = Array.isArray(jsonField?.options)
        && jsonField.options.some((option) => option?.value === "none_of_the_above");

    if (!hasNoneOfTheAboveOption || !value.includes("none_of_the_above")) {
        return "";
    }

    const label = getFieldLabelForRequiredMessage(jsonField)
        .replace(/\s*\(check all that apply\)\s*$/i, "")
        .replace(/[.:\s]+$/g, "")
        || "This selection";

    return `"${label}" cannot have "none of the above" selected along with other values.`;
};

export const getPrevValueFromDiffMap = (dataObj, fieldPath, arrayIndexing = null) => {

    if (!dataObj || !fieldPath) return { value: undefined, has: false };

    const resolvedPath = resolveFieldPath(fieldPath, arrayIndexing);

    // Parse array notation
    const match = resolvedPath.match(/^([^[]+)\[(\d+)\]\.?(.*)$/);
    if (match) {
        const [, arrayName, index, remainder] = match;
        const idx = parseInt(index);

        if (dataObj[arrayName] && dataObj[arrayName][idx]) {
            const element = dataObj[arrayName][idx];

            if (remainder) {
                // For nested paths like "address.pinCode", we need to navigate to the parent object
                const pathParts = remainder.split('.');
                const fieldKey = pathParts.pop();
                const parentPath = pathParts.join('.');

                // Get the parent object that should contain the diffMap
                const parentObj = parentPath ? _.get(element, parentPath) : element;

                // Check if this parent object has a diffMap with our field
                if (parentObj && parentObj.diffMap && fieldKey in parentObj.diffMap) {
                    const previousValue = parentObj.diffMap[fieldKey];
                    const currentValue = parentObj[fieldKey];
                    if (Array.isArray(currentValue) && !Array.isArray(previousValue)) {
                        return { value: undefined, has: false };
                    }
                    return {
                        value: previousValue,
                        has: !areFieldValuesEqual(fieldKey, previousValue, currentValue)
                    };
                }
            } else {
                // A path like "individuals[0]" doesn't point to a specific field; treat as no diff.
                return { value: undefined, has: false };
            }
        }
        return { value: undefined, has: false };
    }

    // Original logic for non-array paths
    const pathParts = resolvedPath.split('.');
    const fieldKey = pathParts.pop();
    const parentPath = pathParts.join('.');
    const parentObj = parentPath ? _.get(dataObj, parentPath) : dataObj;

    if (parentObj && parentObj.diffMap && fieldKey in parentObj.diffMap) {
        const previousValue = parentObj.diffMap[fieldKey];
        const currentValue = parentObj[fieldKey];
        if (Array.isArray(currentValue) && !Array.isArray(previousValue)) {
            return { value: undefined, has: false };
        }
        return {
            value: previousValue,
            has: !areFieldValuesEqual(fieldKey, previousValue, currentValue)
        };
    }
    return { value: undefined, has: false };
};

const NUMBER_VALUE_TYPES = new Set(['number', 'integer', 'long', 'double', 'float', 'decimal']);
const BOOLEAN_VALUE_TYPES = new Set(['boolean']);
const ARRAY_VALUE_TYPES = new Set(['array', 'multipleselection', 'list', 'array<string>', 'list<string>', 'string[]']);

const normalizeFieldToken = (value) => String(value || '').trim().toLowerCase();
const hasSelectableOptions = (fieldConfig) => Array.isArray(fieldConfig?.options)
    && fieldConfig.options.some((option) => _.isPlainObject(option) && option.value !== undefined);

const normalizeBooleanDefaultValue = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === 'true' || normalized === 'yes' || normalized === '1') return true;
        if (normalized === 'false' || normalized === 'no' || normalized === '0') return false;
    }
    return null;
};

const normalizeNumberDefaultValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

export const getFieldSemanticType = (fieldConfigOrType) => {
    if (fieldConfigOrType && typeof fieldConfigOrType === 'object') {
        const valueType = normalizeFieldToken(fieldConfigOrType?.valueType);
        if (valueType === 'checkbox') return hasSelectableOptions(fieldConfigOrType) ? 'array' : 'boolean';
        if (NUMBER_VALUE_TYPES.has(valueType)) return 'number';
        if (BOOLEAN_VALUE_TYPES.has(valueType)) return 'boolean';
        if (ARRAY_VALUE_TYPES.has(valueType)) return 'array';
        if (valueType === 'string' || valueType === 'text' || valueType === 'email' || valueType === 'date' || valueType === 'singleselection' || valueType === 'singleselectionlabel') {
            return 'string';
        }

        const type = normalizeFieldToken(fieldConfigOrType?.type);
        if (type === 'checkbox') return hasSelectableOptions(fieldConfigOrType) ? 'array' : 'boolean';
        if (NUMBER_VALUE_TYPES.has(type)) return 'number';
        if (BOOLEAN_VALUE_TYPES.has(type)) return 'boolean';
        if (ARRAY_VALUE_TYPES.has(type)) return 'array';
        if (type === 'string' || type === 'text' || type === 'email' || type === 'date' || type === 'singleselection' || type === 'singleselectionlabel') {
            return 'string';
        }

        const uiType = normalizeFieldToken(fieldConfigOrType?.uiType);
        if (uiType === 'checkbox') return hasSelectableOptions(fieldConfigOrType) ? 'array' : 'boolean';
        return getFieldSemanticType(uiType);
    }

    switch (normalizeFieldToken(fieldConfigOrType)) {
        case "amountfield":
        case "numberfield":
        case "currencyfield":
        case "percentageinput":
            return 'number';

        case "radioboolean":
        case "checkbox":
            return 'boolean';

        case "checkboxmultiple":
        case "multipleselection":
            return 'array';

        case "input":
        case "textarea":
        case "pincode":
        case "username":
        case "ssn":
        case "ssnmasked":
        case "addressautocomplete":
        case "emailwithvalidation":
        case "select":
        case "countryselect":
        case "countryselectlabel":
        case "radiomultiple":
        case "date":
        case "yearpicker":
        case "yearfield":
        case "phonewithotpvalidation":
        case "phonenumwithvalidation":
        case "signature":
        case "termsacknowledgement":
            return 'string';

        default:
            return null;
    }
};

const getFieldResetFallbackValue = (fieldConfigOrType) => {
    switch (getFieldSemanticType(fieldConfigOrType)) {
        case 'number':
            return 0;
        case 'boolean':
            return null;
        case 'array':
            return [];
        case 'string':
            return "";
        default:
            return null;
    }
};

export const getFieldBaselineValue = (fieldConfigOrType) => {
    if (!fieldConfigOrType) {
        console.warn(`getFieldBaselineValue: Invalid field config '${fieldConfigOrType}', defaulting to null`);
        return null;
    }

    const semanticType = getFieldSemanticType(fieldConfigOrType);
    const emptyValue = semanticType === 'boolean' ? "" : getFieldResetFallbackValue(fieldConfigOrType);
    if (emptyValue === null && semanticType !== 'boolean') {
        const fieldLabel = typeof fieldConfigOrType === 'string'
            ? fieldConfigOrType
            : fieldConfigOrType?.field || fieldConfigOrType?.uiType || fieldConfigOrType?.type;
        console.warn(`getFieldBaselineValue: Unknown field type '${fieldLabel}', defaulting to null`);
    }
    return emptyValue;
};

const normalizeConfiguredDefaultValue = (fieldConfig, defaultValue) => {
    switch (getFieldSemanticType(fieldConfig)) {
        case 'number':
            return normalizeNumberDefaultValue(defaultValue);
        case 'boolean':
            return normalizeBooleanDefaultValue(defaultValue);
        case 'array':
            return Array.isArray(defaultValue) ? defaultValue : [];
        case 'string':
            return defaultValue ?? "";
        default:
            return defaultValue ?? null;
    }
};

export const getFieldResetValue = (jsonField) => {
    if (jsonField && Object.prototype.hasOwnProperty.call(jsonField, 'defaultValue')) {
        return normalizeConfiguredDefaultValue(jsonField, jsonField.defaultValue);
    }

    return getFieldResetFallbackValue(jsonField);
}

export function renderMaskedString(inputString, maskPattern) {
    if (!inputString || !maskPattern) return "";
    let maskedString = '';
    let inputIndex = 0;

    for (let i = 0; i < maskPattern.length; i++) {
        if (maskPattern[i] === 'X' && inputIndex < inputString.length) {
            maskedString += inputString[inputIndex];
            inputIndex++;
        } else {
            maskedString += maskPattern[i];
        }
    }

    return maskedString;
}

export function maskSSN(value, showLast4Only = true) {
    if (!value) return "";

    const cleanValue = value.replace(/-/g, '');

    if (showLast4Only && cleanValue.length === 9) {
        return `XXX-XX-${cleanValue.slice(-4)}`;
    }

    return value;
}

export const getExpandedFields = (iPlusFields, data) => {
    const expandedFields = [];

    for (const field of iPlusFields) {
        if (field.field.includes('[]')) {
            // This is an array field template
            const arrayMatch = field.field.match(/^([^[]+)\[\]/);
            if (arrayMatch) {
                const arrayBasePath = arrayMatch[1];
                const restOfPath = field.field.substring(arrayMatch[0].length); // Everything after []
                const arrayData = _.get(data, arrayBasePath);

                if (Array.isArray(arrayData)) {
                    // Expand for each array item
                    arrayData.forEach((_, index) => {
                        // Build the expanded field path
                        const expandedField = `${arrayBasePath}[${index}]${restOfPath}`;
                        expandedFields.push({
                            ...field,
                            field: expandedField
                        });
                    });
                } else {
                    // If no array data exists yet, still include the template with index 0
                    // This helps with initial validation of required fields
                    const expandedField = `${arrayBasePath}[0]${restOfPath}`;
                    expandedFields.push({
                        ...field,
                        field: expandedField
                    });
                }
            }
        } else {
            // Regular field
            expandedFields.push(field);
        }
    }

    return expandedFields;
};
